import { PaymentTypes } from "../enums";
import { PaymentTypesLabels } from "../labels";

const allPaymentOptions = [
  {
    id: PaymentTypes.Plan,
    name: PaymentTypesLabels[PaymentTypes.Plan],
  },
  {
    id: PaymentTypes.Cash,
    name: PaymentTypesLabels[PaymentTypes.Cash],
  },
  {
    id: PaymentTypes.POSCash,
    name: PaymentTypesLabels[PaymentTypes.POSCash],
  },
  {
    id: PaymentTypes.Card,
    name: PaymentTypesLabels[PaymentTypes.Card],
  },
  {
    id: PaymentTypes.Revolut,
    name: PaymentTypesLabels[PaymentTypes.Revolut],
  },
  {
    id: PaymentTypes.Bank,
    name: PaymentTypesLabels[PaymentTypes.Bank],
  },
  {
    id: PaymentTypes.Multisport,
    name: PaymentTypesLabels[PaymentTypes.Multisport],
  },
  {
    id: PaymentTypes.CoolFit,
    name: PaymentTypesLabels[PaymentTypes.CoolFit],
  },
  {
    id: PaymentTypes.MyPos,
    name: PaymentTypesLabels[PaymentTypes.MyPos],
  },
  {
    id: PaymentTypes.Stripe,
    name: PaymentTypesLabels[PaymentTypes.Stripe],
  },
];

export const getPaymentOptions = () => allPaymentOptions;

/**
 * Reorders `options` to match the order of ids in `orderedTypeIds` (e.g. the
 * order a studio configured its payment methods in). Options whose id isn't
 * present in `orderedTypeIds` are pushed to the end, keeping their relative order.
 */
export const sortPaymentOptionsByTypeOrder = <T extends { id: number }>(
  options: T[],
  orderedTypeIds: number[],
): T[] => {
  const orderIndex = new Map(orderedTypeIds.map((id, index) => [id, index]));

  return [...options].sort(
    (a, b) =>
      (orderIndex.get(a.id) ?? orderedTypeIds.length) -
      (orderIndex.get(b.id) ?? orderedTypeIds.length),
  );
};

export const humanizePaymentType = (
  paymentType?: PaymentTypes,
  planId?: number,
  planName?: string | null
) => {
  // Resolve through the labels record rather than the option list: Deposit
  // (900) is a reservation-only tender that never appears in the option lists
  // but still needs a label on paid reservations, history rows, statistics...
  const label = !!paymentType ? (PaymentTypesLabels[paymentType] ?? "") : "";

  if (paymentType === PaymentTypes.Plan && !!planId) {
    // Show the plan's own name (e.g. "Индивидуален план (#1458)") when we know
    // it; fall back to the generic "Абонаментна карта" label for legacy rows or
    // plans whose name couldn't be resolved (e.g. a deleted plan).
    const namePart = planName?.trim() ? planName.trim() : label;
    return `${namePart} (#${planId})`;
  }

  return label;
};

/** True when MultiSport is one of the studio's payment methods. */
export const studioAcceptsMultisport = (
  supportedPaymentTypes?: PaymentTypes[],
): boolean => !!supportedPaymentTypes?.includes(PaymentTypes.Multisport);

/**
 * True when a MultiSport card can pay for the class: the class takes MultiSport and the
 * studio has MultiSport among its payment methods (the same gate the class page uses).
 */
export const isMultisportAcceptedForEvent = (
  isMultisportAvailable: boolean | undefined,
  supportedPaymentTypes?: PaymentTypes[],
): boolean =>
  !!isMultisportAvailable && studioAcceptsMultisport(supportedPaymentTypes);

/**
 * Returns payment options filtered for direct-sale contexts (cashdesk, card refill).
 * Excludes Plan, Multisport, and CoolFit, then keeps only the types enabled in studio settings.
 */
export const getDirectSalePaymentOptions = (
  studioPaymentTypes?: { paymentType: number }[],
) => {
  const excluded: PaymentTypes[] = [
    PaymentTypes.Plan,
    PaymentTypes.Multisport,
    PaymentTypes.CoolFit,
  ];

  const filtered = allPaymentOptions
    .filter((x) => !excluded.includes(x.id))
    .filter(
      (x) =>
        !studioPaymentTypes ||
        studioPaymentTypes.some((y) => y.paymentType === x.id),
    );

  return sortPaymentOptionsByTypeOrder(
    filtered,
    studioPaymentTypes?.map((x) => x.paymentType) ?? [],
  );
};

/**
 * Payment types that involve handling physical cash and can therefore return
 * change to the customer. Card / electronic payments must be for the exact total.
 */
export const cashChangePaymentTypes: PaymentTypes[] = [
  PaymentTypes.Cash,
  PaymentTypes.POSCash,
];

export const paymentTypesWithCustomPrice: PaymentTypes[] = [
  PaymentTypes.Cash,
  PaymentTypes.POSCash,
  PaymentTypes.Card,
  PaymentTypes.Bank,
  PaymentTypes.Revolut,
  PaymentTypes.MyPos,
];

export const parsePaymentMethodId = (
  rawId: number | string | null | undefined
): { paymentTypeId: PaymentTypes; planTypeId?: number } => {
  if (typeof rawId === "string" && rawId.startsWith("400_")) {
    const [, planTypeIdStr] = rawId.split("_");
    const planTypeId = parseInt(planTypeIdStr, 10);
    return {
      paymentTypeId: PaymentTypes.Plan,
      planTypeId,
    };
  }

  return {
    paymentTypeId: Number(rawId) as PaymentTypes,
  };
};
